import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useAuth } from './AuthContext.js';
import { getAccessToken, api } from '../api/client.js';
import type { RankedOrder, OrderFile, PrintJob } from '@printbridge/shared';

interface WebSocketContextType {
  orders: RankedOrder[];
  setOrders: React.Dispatch<React.SetStateAction<RankedOrder[]>>;
  agentOnline: boolean;
  setAgentOnline: React.Dispatch<React.SetStateAction<boolean>>;
  isConnected: boolean;
  audioAlertEnabled: boolean;
  setAudioAlertEnabled: (enabled: boolean) => void;
  refreshQueue: () => Promise<void>;
  currentAdminShopId: string | null;
  setCurrentAdminShopId: (shopId: string | null) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, shop, isAuthenticated } = useAuth();
  const [orders, setOrders] = useState<RankedOrder[]>([]);
  const [agentOnline, setAgentOnline] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [audioAlertEnabled, setAudioAlertEnabled] = useState(true);
  const [currentAdminShopId, setCurrentAdminShopId] = useState<string | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const expectedSeqRef = useRef<number>(0);

  // Play subtle chime on new order arrival
  const playAlertSound = () => {
    if (!audioAlertEnabled) return;
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, audioCtx.currentTime); // D5
      osc.frequency.setValueAtTime(880, audioCtx.currentTime + 0.1); // A5
      gain.gain.setValueAtTime(0.15, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.35);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start();
      osc.stop(audioCtx.currentTime + 0.35);
    } catch {
      // AudioContext may be restricted by browser gesture policy
    }
  };

  const refreshQueue = async () => {
    const targetShopId = user?.role === 'shopkeeper' ? shop?.id : currentAdminShopId || shop?.id;
    if (!targetShopId) return;

    try {
      const data = await api.getQueue(targetShopId);
      if (Array.isArray(data)) {
        setOrders(data);
      }
    } catch (err) {
      console.warn('Queue refresh failed:', err);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      if (socketRef.current) {
        socketRef.current.close();
      }
      return;
    }

    const targetShopId = user?.role === 'shopkeeper' ? shop?.id : currentAdminShopId || shop?.id;
    if (!targetShopId && user?.role === 'shopkeeper') return;

    let reconnectTimer: NodeJS.Timeout;
    let isClosing = false;

    const connect = () => {
      if (isClosing) return;

      const token = getAccessToken();
      const tokenParam = token ? `token=${encodeURIComponent(token)}&` : '';
      const deviceId = localStorage.getItem('pb_browser_device_id') || '';
      const deviceParam = deviceId ? `deviceId=${encodeURIComponent(deviceId)}&` : '';
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws?${tokenParam}${deviceParam}${targetShopId ? `shopId=${targetShopId}` : ''}`;

      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        refreshQueue();
      };

      ws.onmessage = (event) => {
        try {
          const envelope = JSON.parse(event.data);
          const { type, seq, payload } = envelope;

          // Sequence checking for gap detection
          if (seq && expectedSeqRef.current > 0 && seq > expectedSeqRef.current + 1) {
            console.warn(`[WS] Gap detected: expected ${expectedSeqRef.current + 1}, got ${seq}. Resyncing queue...`);
            refreshQueue();
          }
          if (seq) expectedSeqRef.current = seq;

          if (type === 'queue_snapshot') {
            setOrders(payload.orders || []);
            setAgentOnline(Boolean(payload.agentOnline));
          } else if (type === 'order_upserted') {
            setOrders((prev) => {
              if (payload.status === 'ARCHIVED' || payload.status === 'COMPLETED' || payload.status === 'CANCELLED') {
                return prev.filter((o) => o.id !== payload.id);
              }
              const existingIdx = prev.findIndex((o) => o.id === payload.id);
              if (existingIdx >= 0) {
                const next = [...prev];
                next[existingIdx] = {
                  ...next[existingIdx],
                  ...payload,
                  files: payload.files && payload.files.length > 0 ? payload.files : next[existingIdx].files || [],
                };
                return next;
              } else {
                playAlertSound();
                return [...prev, payload];
              }
            });
          } else if (type === 'order_removed') {
            const targetOrderId = payload.orderId || payload.order_id;
            setOrders((prev) => prev.filter((o) => o.id !== targetOrderId));
          } else if (type === 'file_upserted') {
            const file = payload as OrderFile;
            const targetOrderId = file.orderId || file.order_id;
            setOrders((prev) =>
              prev.map((order) => {
                if (order.id !== targetOrderId) return order;
                const existingFiles = order.files || [];
                const fIdx = existingFiles.findIndex((f) => f.id === file.id);
                const updatedFiles = fIdx >= 0
                  ? existingFiles.map((f, i) => (i === fIdx ? { ...f, ...file } : f))
                  : [...existingFiles, file];
                return { ...order, files: updatedFiles };
              })
            );
          } else if (type === 'file_removed') {
            const targetOrderId = payload.orderId || payload.order_id;
            const targetFileId = payload.fileId || payload.file_id;
            setOrders((prev) =>
              prev.map((order) => {
                if (order.id !== targetOrderId) return order;
                return {
                  ...order,
                  files: (order.files || []).filter((f) => f.id !== targetFileId),
                };
              })
            );
          } else if (type === 'agent_status') {
            setAgentOnline(Boolean(payload.online));
          } else if (type === 'job_updated') {
            const job = payload as PrintJob;
            const targetOrderId = job.orderId || (job as any).order_id;
            if (job.status === 'FAILED') {
              window.dispatchEvent(new CustomEvent('print_job_failed', { detail: job }));
            }
            setOrders((prev) =>
              prev.map((order) => {
                if (order.id !== targetOrderId) return order;
                return {
                  ...order,
                  status: job.status === 'QUEUED' || job.status === 'PRINTING' ? 'PRINTING' : (job.status === 'FAILED' ? 'PENDING' : order.status),
                };
              })
            );
          } else if (type === 'device_revoked') {
            const currentBrowserId = localStorage.getItem('pb_browser_device_id');
            const targetDevId = payload.deviceId;
            const targetUserId = payload.userId;
            if (
              (targetDevId && targetDevId === currentBrowserId) ||
              (targetUserId && user?.id === targetUserId)
            ) {
              console.warn('[WS] Device authorization revoked. Triggering instant security logout.');
              window.dispatchEvent(new CustomEvent('auth_device_revoked', { detail: payload }));
            }
          } else if (type === 'baileys_qr' || type === 'baileys_status' || type === 'whatsapp_status' || type === 'shop_settings_updated' || type === 'switches_updated' || type === 'devices_changed') {
            window.dispatchEvent(new CustomEvent(type, { detail: payload }));
          }
        } catch (err) {
          console.error('[WS Message Error]', err);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        if (!isClosing) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      isClosing = true;
      clearTimeout(reconnectTimer);
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [isAuthenticated, user?.role, shop?.id, currentAdminShopId]);

  return (
    <WebSocketContext.Provider
      value={{
        orders,
        setOrders,
        agentOnline,
        setAgentOnline,
        isConnected,
        audioAlertEnabled,
        setAudioAlertEnabled,
        refreshQueue,
        currentAdminShopId,
        setCurrentAdminShopId,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error('useWebSocket must be used within WebSocketProvider');
  return ctx;
};
